import React, { useEffect, useMemo } from 'react';
import { app, authentication } from '@microsoft/teams-js';

import { Textarea, Button, Label, Spinner } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../store/hooks';

import { submitDialog } from '../../utils/teams';
import { useSendMessageToTeamPageStyles } from './SendMessageToTeamPage.styles';
import {
    selectSendMessageToTeam,
    setIsSubmitting,
    updateMessage,
} from '../../store/features/sendMessageToTeam/sendMessageToTeamSlice';
import { IBotSubmitPayload } from '../../model/IBotSubmitPayload';
import { IBrokerConnectionSendMessageToFlightTeamData } from '../../model/brokerConnections/IBrokerConnectionSendMessageToFlightTeamData';
import BotSubmitAction from '../../model/BotSubmitAction';
import { useParams, useNavigate } from 'react-router';

const SendMessageToTeamPage = () => {
    const { flightTeamId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const styles = useSendMessageToTeamPageStyles();

    const { message, isSubmitting } = useSelector(selectSendMessageToTeam);

    useEffect(() => {
        app.notifySuccess();
    }, []);

    // Detect if the user pasted JSON and whether it parses correctly
    const { looksLikeJson, jsonIsValid } = useMemo(() => {
        const text = (message ?? '').trim();
        const looksLikeJson = text.startsWith('{') || text.startsWith('[');
        let jsonIsValid = true;
        if (looksLikeJson) {
            try {
                JSON.parse(text);
                jsonIsValid = true;
            } catch {
                jsonIsValid = false;
            }
        }
        return { looksLikeJson, jsonIsValid };
    }, [message]);

    const isSubmitDisable = useMemo(() => {
        if (isSubmitting) {
            return true;
        }
        const text = message?.trim() || '';
        const baseValidation = text.length === 0 || text.length > 4000;
        return baseValidation || looksLikeJson;
    }, [isSubmitting, message, looksLikeJson]);

    return (
        <div className={styles.root}>
            <div className={styles.formField}>
                <Label weight="semibold" required>
                    {t('Message')}
                </Label>
                <Textarea
                    rows={5}
                    className={styles.textArea}
                    value={message}
                    onChange={(_, data) => {
                        dispatch(updateMessage(data?.value));
                    }}
                ></Textarea>
                <Label size="small" className={styles.warning}>
                    {t('AskOrTellTeamWarning')}
                </Label>
                {looksLikeJson && (
                    <Label size="small" className={styles.error}>
                        {t('SendMessageToTeam_JsonInvalidWarning')}
                    </Label>
                )}
            </div>
            <div className={styles.actions}>
                {looksLikeJson && jsonIsValid && (
                    <Button
                        appearance="primary"
                        onClick={() => {
                            const text = (message ?? '').trim();
                            // Navigate within the same dialog to misconnected customers page with prefill param.
                            navigate(
                                `/addMisconnectedCustomers/${flightTeamId}?prefill=${encodeURIComponent(
                                    text
                                )}`
                            );
                        }}
                    >
                        {t(
                            'SendMessageToTeam_OpenMisconnectedCustomers',
                            'Open'
                        )}
                    </Button>
                )}
                <Button
                    iconPosition="before"
                    icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                    appearance="primary"
                    disabled={isSubmitDisable || isSubmitting}
                    onClick={async () => {
                        if (isSubmitting) {
                            return;
                        }
                        dispatch(setIsSubmitting(true));
                        const token = await authentication.getAuthToken();

                        const text = message?.trim() || '';
                        const submitRequest: IBotSubmitPayload<IBrokerConnectionSendMessageToFlightTeamData> =
                            {
                                data: {
                                    flightTeamId: flightTeamId!,
                                    message: text!,
                                },
                                appUserAccessToken: token,
                                action: BotSubmitAction.BrokerConnectionSendMessageToFlightTeam,
                            };
                        submitDialog(submitRequest);
                    }}
                >
                    {t('Submit')}
                </Button>
            </div>
        </div>
    );
};

export default SendMessageToTeamPage;
